import { List, Map, OrderedMap } from 'immutable';
import noop from 'lodash/noop';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import * as CatalogConstants from './constants';
import * as D from './deps';

export function getCatalogDetailsFromState(state, pandoraId) {
  return (
    state.entities.getIn(['catalog', D.Constants.Catalog.CatalogKeys.DETAILS, pandoraId]) || Map()
  );
}

export function getArtistDiscographyFromState(state, artistPandoraId) {
  return (
    state.entities.getIn([
      'catalog',
      D.Constants.Catalog.CatalogKeys.DISCOGRAPHY,
      artistPandoraId,
    ]) || List()
  );
}

export function getArtistEventsFromState(state, pandoraId) {
  return (
    state.entities.getIn(['catalog', D.Constants.Catalog.CatalogKeys.EVENTS, pandoraId]) || List()
  );
}

/**
 *
 * @param {object} state
 * @param {string} artistPandoraId
 * @returns {Immutable.List} - A list of trackPandoraIds
 */
export function getAllArtistTracksFromState(state, artistPandoraId) {
  return state.entities.getIn(
    ['catalog', D.Constants.Catalog.CatalogKeys.TRACKS, artistPandoraId],
    List(),
  );
}

export function getAllArtistTracksFromPseudoId(state, artistAllTracksId) {
  const artistId = mapPseudoSourceToParent(state, artistAllTracksId);
  return getAllArtistTracksFromState(state, artistId);
}

export function getAnnotations(state, pandoraId) {
  return (
    state.entities.getIn(['catalog', D.Constants.Catalog.CatalogKeys.ANNOTATIONS, pandoraId]) ||
    Map()
  );
}

function getPandoraIdByShareableUrlPath(path, state) {
  const searchedPath = (path || '').toLowerCase();
  const annotations = D.Utils.Entity.getAllAnnotations(state);
  const annotation =
    annotations &&
    annotations.find(candidate => {
      const candidatePath = (candidate.get('shareableUrlPath') || '').toLowerCase();
      return candidatePath === searchedPath;
    });
  return annotation && annotation.get('pandoraId');
}

const getSourceTypeFromEncryptedId = encryptedId => {
  return encryptedId.substring(0, 2);
};

export const getPandoraIdByEncryptedId = (function () {
  let knownAnnotations = OrderedMap();
  let encryptedUrlToPandoraIdMap = {};
  return function (searchedEncryptedId, state) {
    const annotations = D.Utils.Entity.getAllAnnotations(state);
    if (!knownAnnotations.equals(annotations)) {
      // annotations changed, clear cache so its recalculated
      encryptedUrlToPandoraIdMap = {};
      knownAnnotations = annotations;
    }
    if (!encryptedUrlToPandoraIdMap[searchedEncryptedId]) {
      const annotation =
        annotations &&
        annotations.find(candidate => {
          const candidateSegments = (candidate.get('shareableUrlPath') || '').split('/');
          const candidateEncryptedId = candidateSegments[candidateSegments.length - 1];
          const isSameEncryptedId = candidateEncryptedId === searchedEncryptedId;
          const derivedType = getSourceTypeFromEncryptedId(searchedEncryptedId);
          const isSameType = candidate.get('type') ? derivedType === candidate.get('type') : true;
          return isSameEncryptedId && isSameType;
        });
      encryptedUrlToPandoraIdMap[searchedEncryptedId] = annotation && annotation.get('pandoraId');
    }
    return encryptedUrlToPandoraIdMap[searchedEncryptedId];
  };
})();

export function getAnnotationByShareableUrlPath(state, path) {
  if (!path) {
    return Map();
  }

  const pandoraId = getPandoraIdByShareableUrlPath(path, state);
  if (!pandoraId) {
    return Map();
  }

  return getAnnotations(state, pandoraId);
}

export function getAnnotationByEncryptedPandoraId(state, encryptedId) {
  if (!encryptedId) {
    return Map();
  }

  const pandoraId = getPandoraIdByEncryptedId(encryptedId, state);
  if (!pandoraId) {
    return Map();
  }

  return getAnnotations(state, pandoraId);
}

export function findLinkedPlaylistFromStationId(state, stationId) {
  const annotations = D.Utils.Entity.getAllAnnotations(state);
  return annotations.find(annotation => {
    return annotation.get('linkedSourceId') && annotation.get('linkedSourceId') === stationId;
  });
}

export function findMtupPlaylistFromAnnotation(state) {
  const annotations = D.Utils.Entity.getAllAnnotations(state);
  return annotations.find(annotation => {
    return annotation.get('linkedType') === D.Constants.Catalog.MY_THUMBS_UP_PLAYLIST;
  });
}

export function getIdsOfMissingAnnotations(state, pandoraIds) {
  if (!pandoraIds) return List();
  const missingIds = pandoraIds.filter(pandoraId => {
    return getAnnotations(state, pandoraId).isEmpty();
  });
  return List(missingIds);
}

export function getArtistTopSongs(state, pandoraId) {
  const artist = getCatalogDetailsFromState(state, pandoraId);
  const trackIds = artist.get('topTracks');

  if (!trackIds) {
    return List();
  }
  const tracks = trackIds.map(id => {
    return getAnnotations(state, id);
  });

  return tracks;
}

export function getArtistTopSongsIds(state, pandoraId) {
  const artist = getCatalogDetailsFromState(state, pandoraId);
  const trackIds = artist.get('topTracks');
  return trackIds || List();
}

export function getCurrentSourceId(state) {
  return state.entities.getIn(['catalog', 'currentSourceId']);
}

export const annotationIdVerifier = (state, id) => {
  return state.entities.getIn(['catalog', 'annotations', id], false);
};

export function makeCssColor(rgbColor) {
  return (rgbColor && `#${rgbColor}`) || null;
}

export function getCatalogEntityLink(state, pandoraId) {
  const catalogEntityAnnotations = getAnnotations(state, pandoraId);
  return {
    name: D.Utils.Core.getFromMap(catalogEntityAnnotations, 'name'),
    shareableUrlPath: D.Utils.Core.getFromMap(catalogEntityAnnotations, 'shareableUrlPath'),
  };
}

export function getSimilarArtists(state, artistPandoraId) {
  const catalogArtistDetails = getCatalogDetailsFromState(state, artistPandoraId);
  return D.Utils.Core.getFromMap(catalogArtistDetails, 'similarArtists');
}

export function getLyricSnippet(state, pandoraId) {
  if (!D.Constants.CountryCodes.LyricsCountries.has(D.Utils.TunerVars.getCountryCode(state))) {
    return;
  }
  let snippet;
  const annotations = getAnnotations(state, pandoraId);
  const currentListener = D.Utils.Listener.getCurrentListenerFromState(state);
  const explicitContentFilterEnabled = currentListener
    ? currentListener.get('explicitContentFilterEnabled')
    : false;
  // if the track is clean or the user has turned on their explicit content filter, make sure we show clean lyrics
  if ((annotations && isClean(annotations)) || explicitContentFilterEnabled) {
    snippet = getCatalogDetailsFromState(state, pandoraId).getIn([
      'cleanLyricData',
      'lyricSnippet',
    ]);
  } else {
    snippet = getCatalogDetailsFromState(state, pandoraId).getIn(['lyricData', 'lyricSnippet']);
  }

  return snippet && List(snippet.split('\n'));
}

export function getTracksForAlbum(state, id) {
  return state.entities.getIn(['catalog', 'annotations', id, 'tracks'], List());
}

function hasAllTracksForAlbum(state, id) {
  const tracks = getTracksForAlbum(state, id);
  const trackCount = state.entities.getIn(['catalog', 'annotations', id, 'trackCount'], false);
  return !tracks.isEmpty() && tracks.size === trackCount;
}

export function getTracksForAlbumIfAllArePresent(state, id) {
  return hasAllTracksForAlbum(state, id) ? getTracksForAlbum(state, id) : List();
}

export function getTracksForPlaylist(state, id) {
  const tracks = D.Utils.Music.getTrackObjectsForPlaylist(state, id);
  return List.isList(tracks) ? tracks.map(track => track && track.get('trackPandoraId')) : List();
}

export function getAlbumIdsFromTrackIds(state, trackIds = []) {
  const albumIds = trackIds
    .map(trackId => {
      const annotation = getAnnotations(state, trackId);
      return annotation ? annotation.get('albumId') : null;
    })
    .filter(albumId => albumId);

  return [...new Set(albumIds)];
}

export function getLoadedTracksForPlaylist(state, id) {
  return getTracksForPlaylist(state, id).filter(track => !!track);
}

function getPlaylistPropertyForTrack(state, playlistId, trackPandoraId, property) {
  const tracks = D.Utils.Music.getTrackObjectsForPlaylist(state, playlistId);
  if (tracks && trackPandoraId) {
    const foundTrack = tracks.find(track => {
      return track && track.get('trackPandoraId') === trackPandoraId;
    });
    return foundTrack ? foundTrack.get(property) : null;
  }
  return null;
}

export function getSortIndexForTrack(state, playlistId, trackPandoraId) {
  return getPlaylistPropertyForTrack(state, playlistId, trackPandoraId, 'sortIndex');
}

export function getVersionForPlaylist(state, id) {
  return state.entities.getIn(['playlists', id, 'version'], null);
}

// Internal helper for getTrackGetterBySourceType
function getArtistTopSongIdsFromPseudoId(state, pseudoId) {
  // This relies on returning the original id if we can't map it to a parent id
  // which means we assume the id is the parent id
  return getArtistTopSongsIds(state, mapPseudoSourceToParent(state, pseudoId));
}

function getCollectedAlbumTracksFromPseudoId(state, pseudoId) {
  return D.getInDomain('collection.utils.getCollectedAlbumTracks', noop)(
    state,
    mapPseudoSourceToParent(state, pseudoId),
  );
}

function getSongList(state, id) {
  return List([id]);
}

function getCollectedTracks(state, id, sortOrder) {
  return D.getInDomain('collection.utils.getSongPandoraIds')(state, sortOrder);
}

function getStationThumbedTrackPandoraIds(state, id) {
  const stationId = mapPseudoSourceToParent(state, id);
  return D.Utils.Listener.getStationThumbedTrackIds(state, stationId);
}

export function getTrackGetterBySourceType(type) {
  switch (type) {
    case D.Constants.SourceTypes.SourceTypes.PLAYLIST:
      return getTracksForPlaylist;
    case D.Constants.SourceTypes.SourceTypes.ALBUM:
      return getTracksForAlbum;
    case D.Constants.SourceTypes.SourceTypes.ARTIST_TOP_SONGS:
      return getArtistTopSongIdsFromPseudoId;
    case D.Constants.SourceTypes.SourceTypes.ARTIST_ALL_SONGS:
      return getAllArtistTracksFromPseudoId;
    case D.Constants.SourceTypes.SourceTypes.SONG:
      return getSongList;
    case D.Constants.SourceTypes.SourceTypes.COLLECTED_ALBUM_TRACKS:
      return getCollectedAlbumTracksFromPseudoId;
    case D.Constants.SourceTypes.SourceTypes.COLLECTED_TRACKS:
      return getCollectedTracks;
    case D.Constants.SourceTypes.SourceTypes.STATION_THUMB_UP:
      return getStationThumbedTrackPandoraIds;
  }
  return noop;
}

export function mapPseudoSourceToParent(state, id) {
  const sourceType = D.Utils.SourceType.getSourceTypeFromPandoraId(id);

  if (D.Constants.SourceTypes.ArtistPseudoSourceTypes.includes(sourceType)) {
    const artistPseudoPieces = id.split(':');
    const artistPrefix = D.Constants.SourceTypes.PseudoObfuscationTypes[artistPseudoPieces[1]];
    return `${artistPrefix}:${artistPseudoPieces[2]}`;
  } else if (D.Constants.SourceTypes.SourceTypes.COLLECTED_TRACKS === sourceType) {
    return D.Utils.Listener.getCurrentListenerIdFromState(state);
  } else if (D.Constants.SourceTypes.SourceTypes.COLLECTED_ALBUM_TRACKS === sourceType) {
    const albumPseudoPieces = id.split(':');
    return `${D.Constants.SourceTypes.PandoraTypes.ALBUM}:${
      albumPseudoPieces[albumPseudoPieces.length - 1]
    }`;
  } else if (D.Constants.SourceTypes.SourceTypes.STATION_THUMB_UP === sourceType) {
    return id.split(':').pop();
  }

  return id;
}

export function getAllAnnotationsForArtist(state, pandoraId) {
  const annotations = D.Utils.Entity.getAllAnnotations(state);
  let artistAnnotations = new List();
  annotations.map(annotation => {
    if (
      annotation.get('artistId') &&
      annotation.get('artistId') === pandoraId &&
      !artistAnnotations.includes(annotation)
    ) {
      artistAnnotations = artistAnnotations.push(annotation);
    }
  });
  return artistAnnotations;
}

export function getIsRightsInfoExpired(state, pandoraId) {
  const entity = getAnnotations(state, pandoraId);
  const entityRightsInfoExpiryTimestamp = entity.getIn(['rightsInfo', 'expirationTime']);
  const currentTimestamp = Math.floor(Date.now());
  if (currentTimestamp >= entityRightsInfoExpiryTimestamp) {
    return true;
  }
  return false;
}

export function getFirstIndexThatHasInteractiveRights(state, trackIds) {
  let index;
  for (let i = 0; i < trackIds.size; i++) {
    const track = getAnnotations(state, trackIds.get(i));
    if (track.getIn(['rightsInfo', 'hasInteractive']) === true) {
      index = i;
      break;
    }
  }
  return index;
}

export function getRadioUnavailableMessage(state, trackIds) {
  let radioOnly = false;
  let unavailableOnly = false;
  for (let i = 0; i < trackIds.size; i++) {
    const track = getAnnotations(state, trackIds.get(i));
    if (track.getIn(['rightsInfo', 'hasInteractive']) === true) {
      radioOnly = unavailableOnly = false;
      break;
    } else if (D.Utils.Rights.isRadioOnly(track)) {
      radioOnly = true;
    } else if (D.Utils.Rights.isUnavailable(track)) {
      unavailableOnly = true;
    }
  }

  let result = null;
  if (radioOnly) {
    // if rights are a mix of radio-only and unavailable with no interactive tracks, radio-only wins for the message
    result = D.Constants.Catalog.messages.radioOnlyPlaylist;
  } else if (unavailableOnly) {
    result = D.Constants.Catalog.messages.unavailablePlaylist;
  }
  return result;
}

export function isClean(entity) {
  return entity.get('explicitness') === CatalogConstants.Explicitness.CLEAN;
}

export function isExplicit(entity) {
  return entity.get('explicitness') === CatalogConstants.Explicitness.EXPLICIT;
}

export function isCatalogVoiceTrack(trackAnnotation) {
  return (
    !!trackAnnotation &&
    !!trackAnnotation.get('pandoraId') &&
    trackAnnotation.get('type') === D.Constants.SourceTypes.PandoraTypes.VOICE_TRACK
  );
}

export function isCatalogHostedStation(state, pandoraId) {
  return !!getAnnotations(state, pandoraId).get('isHosted');
}

export function isHostedPlaylist(state, playlistId) {
  let annotations = getAnnotations(state, playlistId);
  if (annotations.size === 0) {
    annotations = state.entities.getIn(['playlists', playlistId], Map());
  }
  const includedTrackTypes = annotations.get('includedTrackTypes', List());
  return includedTrackTypes.contains(D.Constants.SourceTypes.PandoraTypes.VOICE_TRACK);
}

export function isHosted(state, pandoraId) {
  return (
    pandoraId && (isHostedPlaylist(state, pandoraId) || isCatalogHostedStation(state, pandoraId))
  );
}

export function isVariousArtists(artistAnnotations) {
  if (!artistAnnotations) {
    return false;
  }
  return (
    artistAnnotations.get('pandoraId') === CatalogConstants.VARIOUS_ARTISTS_ARTIST_ID ||
    artistAnnotations.get('variousArtist')
  );
}

export function isSingleRelease(albumAnnotations) {
  return (
    albumAnnotations && albumAnnotations.get('releaseType') === CatalogConstants.ReleaseType.SINGLE
  );
}

export function isEpRelease(albumAnnotations) {
  return (
    albumAnnotations && albumAnnotations.get('releaseType') === CatalogConstants.ReleaseType.EP
  );
}

export function getTrackAuthor(state, trackAnnotation) {
  if (isCatalogVoiceTrack(trackAnnotation)) {
    const authorId = trackAnnotation.get('authorId');
    const authorAnnotation = authorId && getAnnotations(state, authorId);
    return (authorAnnotation && authorAnnotation.get('name')) || '';
  }
  return trackAnnotation.get('artistName');
}

export function getTrackAuthorShareableUrlPath(state, trackAnnotation) {
  const authorId = isCatalogVoiceTrack(trackAnnotation)
    ? trackAnnotation.get('authorId')
    : trackAnnotation.get('artistId');

  const author = getAnnotations(state, authorId);
  return author.get('shareableUrlPath', null);
}

export function buildTrackAuthorsMap(state, trackAnnotations) {
  return trackAnnotations.reduce((authors, track) => {
    const trackId = track.get('pandoraId');
    const authorName = getTrackAuthor(state, track);
    return authors.set(trackId, authorName);
  }, Map());
}

export function getPremiumSourceArtistId(state, pandoraId) {
  if (
    D.Constants.SourceTypes.ArtistPseudoSourceTypes.includes(
      D.Utils.SourceType.getSourceTypeFromPandoraId(pandoraId),
    )
  ) {
    return mapPseudoSourceToParent(state, pandoraId);
  }

  const annotations = getAnnotations(state, pandoraId);
  return annotations.get('artistId');
}

const Frequency = {
  monthly: 'monthly',
  allTime: 'allTime',
};

function calcDisplayListenerCount(listenerCount, displayZero, qaTag, frequency) {
  if ((!listenerCount && listenerCount !== 0) || (displayZero === false && listenerCount === 0)) {
    return '';
  }
  const message =
    frequency === Frequency.monthly
      ? D.Constants.Station.StationMessages.monthlyListeners
      : D.Constants.Station.StationMessages.listeners;

  const abbreviatedListenerCount = D.Utils.NumberUtils.numberToHumanSize(listenerCount) + ' ';
  return (
    <span data-qa={qaTag}>
      {abbreviatedListenerCount}
      <FormattedMessage {...message} values={{ listenerCount }} />
    </span>
  );
}

export function displayListenerCount(listenerCount, displayZero = false, qaTag) {
  return calcDisplayListenerCount(listenerCount, displayZero, qaTag, Frequency.allTime);
}

export function displayMonthlyListenerCount(listenerCount, displayZero = false, qaTag) {
  return calcDisplayListenerCount(listenerCount, displayZero, qaTag, Frequency.monthly);
}
